import { ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import { AlertColor } from '@mui/material';

import { AUTHORIZATION_TOKEN, CORRELATION_ID } from '../../constants/storage';
import errors from '../../constants/errors';
import { NO_AUTH_PATH } from '../routes/paths';
import { nortadaLogger } from '../common/logger';

export interface LogoutMessage {
  text: string;
  severity: AlertColor;
  correlationId?: string | null;
}

interface Options {
  redirect: (path: string, message: LogoutMessage) => void;
  handleAuthInit?: () => void;
  handleChangeAuthState: (isAuth: boolean) => void;
}

let keycloak: Keycloak.KeycloakInstance;
let options: Options;
const keycloakLogin = () => {
  const { redirectUrl } = window.nortada.keycloakAuthentication;

  return keycloak.init({
    onLoad: 'login-required',
    redirectUri: redirectUrl,
    silentCheckSsoRedirectUri: redirectUrl,
    pkceMethod: 'S256'
  });
};

const logout = (message?: LogoutMessage) => {
  localStorage.removeItem(AUTHORIZATION_TOKEN);
  localStorage.removeItem(CORRELATION_ID);
  options.handleChangeAuthState(false);

  if (message) {
    options.redirect(NO_AUTH_PATH.LOGOUT_REDIRECT, message);
  } else {
    keycloak.logout();
  }
};

const initKeycloakSession = async ({ onSuccess }: { onSuccess: () => void }) => {
  try {
    const { redirectUrl } = window.nortada.keycloakAuthentication;
    const authToken = localStorage.getItem(AUTHORIZATION_TOKEN);
    if (!authToken) {
      return false;
    }
    const success = await keycloak.init({
      redirectUri: redirectUrl,
      token: authToken,
      refreshToken: authToken
    });
    if (!success) {
      throw new Error();
    }
    await onSuccess();
    return success;
  } catch (e) {
    throw new Error();
  }
};

export const authentication = {
  verifyAuthProviderConnection(onSuccess: () => void, onError: (message: ReactNode) => void) {
    const { url, realm } = window.nortada.keycloakAuthentication;

    fetch(`${url}/realms/${realm}`)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        onError(errors.noKeycloakConnection);
      });
  },
  initialize: (_options: Options) => {
    const { url, realm, clientId } = window.nortada.keycloakAuthentication;
    options = _options;
    keycloak = Keycloak({
      realm,
      clientId,
      url
    });
    if (options.handleAuthInit) {
      options.handleAuthInit();
    }
  },
  login: () => keycloakLogin(),
  loginRedirect: () => {
    keycloakLogin();
    keycloak.login({ prompt: 'login' });
  },
  logout,
  userProfile: () => keycloak.loadUserProfile(),
  updateToken: (): Promise<boolean> =>
    new Promise(resolve => {
      keycloak
        .updateToken(5)
        .then(refreshed => {
          if (refreshed) {
            const authToken = keycloak.token;
            if (authToken) {
              localStorage.setItem(AUTHORIZATION_TOKEN, authToken);
            }
            resolve(true);
            nortadaLogger.info('Successfully get a new token.');
          } else {
            nortadaLogger.info('Token is still valid.');
            resolve(false);
          }
        })
        .catch(() => {
          resolve(false);
          nortadaLogger.error('Failed get a new token.');
        });
    }),
  get token() {
    return keycloak.token;
  },
  initKeycloakSession
};
